import { useEffect, useState } from 'react'
import { Users } from 'lucide-react'
import { apiGet, usePermissions } from '../admin/primitives.js'
import { useSoiree } from './SoireeData.js'
import { useMenuPersonne } from '../rh/menu-personne.js'
import '../rh/presence.css'

/**
 * PERSONNEL D'UNE SOIRÉE.
 *
 * Cette tuile ne calcule RIEN. Elle lit `/api/v1/hr/presence/event/:id`, qui
 * sert exactement le même read model que l'écran RH Présence et que la borne
 * iPad. Écrire ici une seconde requête métier — « compter les shifts de la
 * nuit », par exemple — donnerait deux chiffres différents pour la même
 * soirée, et personne ne saurait lequel croire.
 *
 * DEUX HONNÊTETÉS D'AFFICHAGE :
 *
 * · Un shift au planning n'est PAS une présence. « 17 présents » compte les
 *   personnes qui ont réellement pointé, pas celles qui étaient prévues.
 * · Le rattachement d'un service à une soirée est EXPLICITE. Une personne qui
 *   travaille cette nuit-là sans que son service soit rattaché n'apparaît pas
 *   ici — et c'est voulu : rapprocher par simple coïncidence de date
 *   inventerait un lien que personne n'a décidé.
 */

type Etat = 'OFF_SHIFT' | 'UPCOMING_SHIFT' | 'EXPECTED_LATE' | 'WORKING' | 'WORKING_UNPLANNED' | 'ON_BREAK' | 'FINISHED'

interface Personne {
  person_id: string; display_name: string; position_name: string | null
  state: Etat; entry_signed: boolean
  clock_in_at: string | null
  shift: { planned: boolean; location_name: string | null } | null
}
interface Reponse {
  counts: Record<Etat, number> & { total: number; present: number }
  people: Personne[]
  planned_shifts: number
}

const LABEL: Record<Etat, { texte: string; ton: string }> = {
  WORKING: { texte: 'En poste', ton: 'emerald' },
  WORKING_UNPLANNED: { texte: 'Hors planning', ton: 'violet' },
  ON_BREAK: { texte: 'En pause', ton: 'cyan' },
  EXPECTED_LATE: { texte: 'Pas encore arrivé', ton: 'amber' },
  UPCOMING_SHIFT: { texte: 'Attendu', ton: 'blue' },
  FINISHED: { texte: 'Terminé', ton: 'slate' },
  OFF_SHIFT: { texte: 'Hors service', ton: 'slate' },
}
const heure = (iso: string | null): string =>
  iso ? new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' }).format(new Date(iso)) : '—'

export function SoireePersonnelWidget() {
  const { id } = useSoiree()
  const perms = usePermissions()
  const menu = useMenuPersonne()
  const [data, setData] = useState<Reponse | null>(null)
  const [erreur, setErreur] = useState(false)

  useEffect(() => {
    if (!id) return
    let vivant = true
    const lire = async () => {
      try {
        const r = await apiGet<Reponse>(`/api/v1/hr/presence/event/${id}`)
        if (vivant) { setData(r); setErreur(false) }
      } catch { if (vivant) setErreur(true) }
    }
    void lire()
    const t = setInterval(() => { void lire() }, 30_000)
    return () => { vivant = false; clearInterval(t) }
  }, [id])

  if (!perms.permissions.includes('time.read.team')) {
    return <p className="pr-vide">Le personnel de la soirée demande le droit de lire le temps de l’équipe.</p>
  }
  if (erreur) return <p className="pr-vide">Personnel indisponible pour le moment.</p>
  if (!data) return <p className="pr-vide">Chargement…</p>

  if (data.people.length === 0) {
    return (
      <p className="pr-vide">
        Aucun service n’est rattaché à cette soirée. Le rattachement se fait depuis le planning,
        sur chaque service — Gestion ne le déduit jamais d’une date.
      </p>
    )
  }

  const c = data.counts
  return (
    <div>
      <div className="pr-kpis">
        <div className="pr-kpi pr-kpi--slate"><span className="pr-kpi__n">{c.total}</span><span className="pr-kpi__l">Prévus</span></div>
        <div className="pr-kpi pr-kpi--emerald"><span className="pr-kpi__n">{c.present}</span><span className="pr-kpi__l">Présents</span></div>
        <div className="pr-kpi pr-kpi--cyan"><span className="pr-kpi__n">{c.ON_BREAK}</span><span className="pr-kpi__l">En pause</span></div>
        <div className="pr-kpi pr-kpi--amber"><span className="pr-kpi__n">{c.EXPECTED_LATE}</span><span className="pr-kpi__l">Pas encore arrivés</span></div>
      </div>
      <ul className="pr-liste">
        {data.people.map((p) => (
          <li key={p.person_id} className="pr-carte" {...menu.personne({ id: p.person_id, nom: p.display_name })}>
            <div className="pr-carte__id">
              <span className="pr-nom">{p.display_name}</span>
              <span className="pr-meta">{[p.position_name, p.shift?.location_name].filter(Boolean).join(' · ') || '—'}</span>
            </div>
            <div className="pr-carte__etat">
              <span className={`pr-etat pr-etat--${LABEL[p.state].ton}`}><i aria-hidden="true" />{LABEL[p.state].texte}</span>
              {p.shift && !p.shift.planned ? <span className="pr-tag pr-tag--violet">Non prévu</span> : null}
              {p.entry_signed ? <span className="pr-tag pr-tag--ok">Signée</span> : null}
            </div>
            <div className="pr-carte__horaires"><span><b>Entrée</b> {heure(p.clock_in_at)}</span></div>
            <div />
          </li>
        ))}
      </ul>
      <p className="pr-meta" style={{ marginTop: 8 }}>
        <Users size={11} aria-hidden="true" /> « Présents » compte les personnes qui ont réellement pointé,
        pas celles qui étaient prévues.
      </p>
    </div>
  )
}
